import { Fragment, useState } from "react";

import { formatEmailContact, personName } from "./ticketHelpers";

interface SenderContact {
  givenName?: string | null;
  familyName?: string | null;
  name?: string | null;
  emailAddress?: string | null;
}

/**
 * Shows the sender's name; a click swaps it for the e-mail address (and back).
 * Falls back to the address when there is no name, and renders plain text when
 * there is nothing to switch to.
 */
export default function SenderName({
  contact,
  hideAddress = false,
}: {
  contact: SenderContact | null | undefined;
  /** Name only, address on click. Off: the full "Name <address>" as before. */
  hideAddress?: boolean;
}) {
  const [showAddress, setShowAddress] = useState(false);
  if (!contact) return null;
  if (!hideAddress) return <span>{formatEmailContact(contact)}</span>;

  const name = (contact.name ?? personName(contact) ?? "").trim();
  const address = contact.emailAddress ?? "";
  if (!name || !address) return <span>{name || address}</span>;

  return (
    <button
      type="button"
      className="cursor-pointer text-left hover:underline"
      title={showAddress ? "Namen anzeigen" : "E-Mail-Adresse anzeigen"}
      onClick={() => {
        setShowAddress((prev) => !prev);
      }}
    >
      {showAddress ? address : name}
    </button>
  );
}

const MAX_RECIPIENTS = 5;

/** Comma-separated recipient names, each switchable to its address. */
export function RecipientNames({
  contacts,
  hideAddress = false,
}: {
  contacts: (SenderContact | null | undefined)[];
  hideAddress?: boolean;
}) {
  return (
    <>
      {contacts.slice(0, MAX_RECIPIENTS).map((contact, index) => (
        <Fragment key={contact?.emailAddress ?? index}>
          {index > 0 && ", "}
          <SenderName contact={contact} hideAddress={hideAddress} />
        </Fragment>
      ))}
      {contacts.length > MAX_RECIPIENTS && "..."}
    </>
  );
}
